/**
 * Tax Output / Tax Input report (BE-14a)
 *
 * The VAT on sales (output) or on purchases (input) for a period, one row per journal entry by tax point date.
 * A credit note or a correction is negative; the totals tie to the VAT return's boxes for the same period.
 * Shown as tabs of Billing › Tax: /reports/vat-return?tab=output|input
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Alert, Chip } from '@mui/material';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { PeriodSelector, SummaryCardGrid, DataTable, CurrencyText, ReportExportMenu } from '../../components/accounting';
import { formatDate } from '../../utils/accounting/formatters';
import { getPeriodDates, toYmd } from '../../utils/accounting/periodUtils';
import { sourceInfo } from '../../utils/accounting/journalSources';
import { serverMessage } from '../../utils/apiConvert';
import { financeReportsService } from '../../services/accounting/financeReports.service';

const COPY = {
  output: { title: 'Tax Output Report', desc: 'VAT charged on sales — the VAT return\'s output tax' },
  input: { title: 'Tax Input Report', desc: 'Recoverable VAT on purchases — the VAT return\'s input tax' },
};

const COLUMNS = [
  { key: 'taxPointDate', label: 'Tax Point', width: '11%', render: (r) => formatDate(r.taxPointDate) },
  { key: 'reference', label: 'Reference', width: '16%', render: (r) => <Typography sx={{ fontSize: fs.xs, fontFamily: 'monospace' }}>{r.reference}</Typography> },
  {
    key: 'sourceType', label: 'Type', width: '11%',
    render: (r) => {
      const s = sourceInfo(r.sourceType);
      return <Chip label={s.label} size="small" sx={{ height: 20, fontSize: '0.6875rem', bgcolor: `${s.bg}22`, color: s.bg }} />;
    },
  },
  {
    key: 'description', label: 'Description', width: '30%',
    render: (r) => (
      <Box>
        <Typography sx={{ fontSize: fs.sm }}>{r.description}</Typography>
        {r.counterparty && <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>{r.counterparty}</Typography>}
      </Box>
    ),
  },
  { key: 'base', label: 'Base', width: '11%', align: 'right', render: (r) => <CurrencyText amount={r.base} /> },
  { key: 'vat', label: 'VAT', width: '10%', align: 'right', render: (r) => <CurrencyText amount={r.vat} bold /> },
  { key: 'total', label: 'Total', width: '11%', align: 'right', render: (r) => <CurrencyText amount={r.total} /> },
];

const TaxReport = ({ direction }) => {
  const [period, setPeriod] = useState('THIS_MONTH');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { from, to } = getPeriodDates(period);
      setData(await financeReportsService.getTaxReport(direction, { from: toYmd(from), to: toYmd(to) }));
    } catch (err) {
      setData(null);
      setError(serverMessage(err, `Failed to load the ${COPY[direction].title.toLowerCase()}`));
    } finally {
      setLoading(false);
    }
  }, [direction, period]);

  useEffect(() => { load(); }, [load]);

  const cards = [
    { label: 'Entries', value: data?.count ?? 0, suffix: 'entries' },
    { label: 'Taxable Base', value: Number(data?.baseTotal || 0) },
    { label: direction === 'output' ? 'Output VAT' : 'Input VAT', value: Number(data?.vatTotal || 0), color: direction === 'output' ? 'warning' : 'success' },
    { label: 'Total incl. VAT', value: Number(data?.total || 0) },
  ];

  return (
    <Box sx={{ p: 2, width: 0, minWidth: '100%', boxSizing: 'border-box' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography sx={{ fontSize: fs.md, fontWeight: fw.semibold }}>{COPY[direction].title}</Typography>
          <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>
            {COPY[direction].desc}{data ? ` · ${formatDate(data.from)} – ${formatDate(data.to)}` : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <PeriodSelector value={period} onChange={setPeriod} />
          <ReportExportMenu reportType={`tax-${direction}`} params={{ from: data?.from, to: data?.to }} onError={setError} />
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}

      <SummaryCardGrid cards={cards} loading={loading && !data} />

      <Box sx={{ mt: 1.5 }}>
        <DataTable
          columns={COLUMNS}
          data={(data?.rows || []).map((r, i) => ({ ...r, id: `${r.reference}-${i}` }))}
          loading={loading}
          emptyMessage="No VAT in this period."
        />
      </Box>
      <Typography sx={{ fontSize: fs.xs, color: 'text.secondary', mt: 1 }}>
        By tax point date. A credit note or a correction shows as negative. The totals match the VAT return for the same period.
      </Typography>
    </Box>
  );
};

export default TaxReport;
